import { Component, computed, inject, OnInit, signal, Signal, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ModalComponent, ToastrService } from '@kiwid-app/kiwid-ui-lib-angular';
import { BaseComponent } from 'app/layout/common/base/base.component';
import { IOrder } from 'app/core/models/order/order';
import { locale as english } from './i18n/en';
import { locale as spanish } from './i18n/es';
import { locale as portuguese } from './i18n/pt';
import { RatingService } from 'app/core/services/order/rating.service';
import { first } from 'rxjs';
import { ErrorHelper } from 'app/core/helpers/error.helper';
import { IRatingType } from 'app/core/models/order/rating';
import { MatIcon } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslatePipe } from '@ngx-translate/core';
import { MatInput } from '@angular/material/input';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { OrderService } from 'app/core/services/order/order.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

@Component({
    selector: 'app-modal-create-rating',
    imports: [MatIcon, MatFormFieldModule, TranslatePipe, MatInput, ReactiveFormsModule, NgxSkeletonLoaderModule],
    templateUrl: './modal-create-rating.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './modal-create-rating.component.scss',
})
export class ModalCreateRatingComponent extends BaseComponent implements OnInit {
    private readonly ratingService = inject(RatingService);
    private readonly orderService = inject(OrderService);
    private readonly toastrService = inject(ToastrService);
    private readonly formBuilder = inject(FormBuilder);

    public dialog: Signal<MatDialogRef<ModalComponent, string>>;
    public order: Signal<IOrder>;

    protected readonly ratingTypes = signal<IInternalRatingType[]>([]);
    protected readonly isLoading = signal(true);
    protected readonly renderOrder = computed(() => ({
        laboratory: {
            name: this.order().location.name,
            avatar: this.order().laboratory.logo?.url,
        },
        patient: !!this.order().patient ? this.order().patient?.kFullName() : null,
    }));
    protected readonly formRating = signal(
        this.formBuilder.nonNullable.group({
            comment: this.formBuilder.control(''),
        }),
    );

    ngOnInit() {
        this.registerOnTranslate('MODAL_CREATE_RATING', portuguese, english, spanish);
        this.registerButtonModal();
        this.getData();
    }

    private submit(): void {
        if (this.formRating().invalid || this.dialog().componentInstance.findButton(1).isLoading) {
            this.formRating().markAllAsTouched();
            return;
        }
        this.dialog().componentInstance.updateButton(1, { isLoading: true });

        const ratings = this.ratingTypes().map((ratingType) => {
            return {
                ratingTypeId: ratingType.id,
                value: ratingType.value,
            };
        });

        this.subscriptions.add(
            this.orderService
                .createRating({
                    orderId: this.order().id,
                    ratings,
                    note: this.formRating().getRawValue().comment ?? null,
                })
                .pipe(first())
                .subscribe({
                    next: ({ data, message }) => {
                        this.toastrService.show({ type: 'success', message });
                        this.dialog().close(JSON.stringify(data));
                        this.dialog().componentInstance.updateButton(1, { isLoading: false });
                    },
                    error: (error) => {
                        this.dialog().componentInstance.updateButton(1, { isLoading: false });
                        ErrorHelper.setToastrErrorModal(this.dialog().componentInstance, error);
                    },
                }),
        );
    }

    protected setRatingValue(id: string, number: number): void {
        this.ratingTypes.update((rating) =>
            rating.map((ratingType) => {
                if (ratingType.id === id) {
                    return {
                        ...ratingType,
                        value: number,
                    };
                }
                return ratingType;
            }),
        );
    }

    private getData(): void {
        this.subscriptions.add(
            this.ratingService
                .getRatingTypes()
                .pipe(first())
                .subscribe({
                    next: ({ data }) => {
                        this.ratingTypes.set(
                            data.map((ratingType) => {
                                return {
                                    ...ratingType,
                                    value: 0,
                                };
                            }),
                        );
                        this.isLoading.set(false);
                    },
                    error: (error) => {
                        this.isLoading.set(false);
                        ErrorHelper.setToastrErrorModal(this.dialog().componentInstance, this.toastrService);
                    },
                }),
        );
    }

    private registerButtonModal(): void {
        this.dialog().componentInstance.buttons = [
            {
                id: 2,
                text: this.translateKey('MODAL.BUTTONS.CANCEL'),
                type: 'secondary',
            },
            {
                id: 1,
                text: this.translateKey('MODAL.BUTTONS.SAVE'),
                type: 'primary',
            },
        ];

        this.subscriptions.add(
            this.dialog().componentInstance.buttonEventClick.subscribe((button) => {
                if (this.dialog().componentInstance.buttons.some((button) => button.isLoading)) {
                    return;
                }

                if (button.id === 1) {
                    this.submit();
                    return;
                }

                this.dialog().close();
            }),
        );
    }
}

type IInternalRatingType = IRatingType & {
    value: number;
};
